import { initSentry } from '@/lib/sentry';

initSentry();

import '@/lib/i18n';
import './index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AppToaster } from '@/components/AppToaster';
import { AuthProvider } from '@/contexts/AuthContext';
import { PageMetaProvider } from '@/contexts/PageMetaContext';
import { ShopProvider } from '@/contexts/ShopContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { router } from '@/router';
import { createShopNavigation } from '@/lib/shopSwitchNavigation';
import { CookieConsent } from '@/components/cookie-consent';
import { trackSpaPageView } from '@/lib/meta-pixel';
import { purgeStorefrontResponses, unregisterServiceWorkerInBrowser } from '@/lib/pwa';

// Hors PWA installée : on retire tout Service Worker résiduel pour qu'une simple
// actualisation charge toujours la dernière version (cf. src/lib/pwa.ts).
void unregisterServiceWorkerInBrowser();
void purgeStorefrontResponses();

// Meta Pixel — PageView sur chaque navigation SPA. Le premier appel fixe la baseline
// (le PageView initial est émis par loadMetaPixel() après consentement cookies).
trackSpaPageView(router.state.location.pathname);
router.subscribe((state) => trackSpaPageView(state.location.pathname));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const shopNavigation = createShopNavigation(router, queryClient);

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

const app = (
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ShopProvider navigation={shopNavigation}>
              <PageMetaProvider>
                <RouterProvider router={router} />
              </PageMetaProvider>
              <CookieConsent />
              <AppToaster />
            </ShopProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>
);

// Rendu client (createRoot), y compris sur les pages marketing prerendues (SSG).
// On n'utilise PAS hydrateRoot : le HTML prerendu sert aux crawlers/IA (qui ne lisent que
// le HTML initial) et au premier rendu, mais l'app contient des composants non
// déterministes à l'hydratation (animations `motion`, classe de thème) qui provoquaient des
// mismatches React #418/#423. createRoot re-rend proprement le même contenu — pas d'erreur,
// flash négligeable (contenu identique), et le SEO reste assuré par le snapshot statique.
createRoot(rootElement).render(app);
